<template>
    <div class="inventory-tickets">
        <!-- Header with Stats - Enhanced with animated counters -->
        <div class="stats-panel">
            <div class="stat-card" v-for="stat in ticketStats" :key="stat.label" :class="stat.color">
                <div class="stat-icon-wrapper">
                    <div class="stat-icon">
                        <span class="stat-emoji">{{ stat.emoji }}</span>
                    </div>
                </div>
                <div class="stat-content">
                    <div class="stat-number">
                        <animated-number :value="stat.value" :duration="500" />
                    </div>
                    <div class="stat-label">{{ stat.label }}</div>
                    <div class="stat-trend" v-if="stat.trend">
                        <span :class="stat.trend > 0 ? 'trend-up' : 'trend-down'">
                            {{ stat.trend > 0 ? '↑' : '↓' }} {{ Math.abs(stat.trend) }}%
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Main Card with Glassmorphism Effect -->
        <div class="tickets-card">
            <!-- Card Header -->
            <div class="card-header">
                <div class="header-left">
                    <div class="title-wrapper">
                        <div class="title-icon">🎫</div>
                        <h2 class="card-title">Service Tickets</h2>
                    </div>
                    <div class="ticket-count-pill">
                        <span class="count-number">{{ items.length }}</span>
                        <span class="count-label">total</span>
                    </div>
                </div>
                <div class="header-right">
                    <div class="search-wrapper">
                        <svg class="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="2"/>
                            <path d="M21 21L17 17" stroke="currentColor" stroke-width="2"/>
                        </svg>
                        <input 
                            type="text" 
                            v-model="searchQuery" 
                            placeholder="Search by invoice or product..."
                            class="search-input"
                        >
                        <button class="search-clear" v-if="searchQuery" @click="searchQuery = ''">✕</button>
                    </div>
                    <button class="filter-toggle" @click="showFilters = !showFilters">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M4 6H20M6 12H18M10 18H14" stroke="currentColor" stroke-width="2"/>
                        </svg>
                    </button>
                </div>
            </div>

            <!-- Filter Section - Modern Chip Design -->
            <div class="filter-section" :class="{ 'expanded': showFilters }">
                <div class="filter-tabs">
                    <button 
                        v-for="option in list_options" 
                        :key="option.value"
                        :class="['filter-chip', { active: list_option_type === option.value }]"
                        @click="list_option_type = option.value; fetch_items()"
                    >
                        <span class="chip-icon">{{ getStatusIcon(option.value) }}</span>
                        {{ option.text }}
                        <span class="chip-count">{{ getStatusCount(option.value) }}</span>
                    </button>
                </div>
            </div>

            <!-- Enhanced Table with Modern Design -->
            <div class="table-container">
                <b-overlay :show="items_being_loaded" spinner-variant="primary" spinner-large>
                    <div v-if="filteredItems.length > 0" class="tickets-table">
                        <div class="table-header">
                            <div class="header-cell sortable" @click="sortby('created_on')">
                                <span>📅 Created</span>
                                <span class="sort-icon">{{ getSortIcon('created_on') }}</span>
                            </div>
                            <div class="header-cell sortable" @click="sortby('invoice_id')">
                                <span>🧾 Invoice</span>
                                <span class="sort-icon">{{ getSortIcon('invoice_id') }}</span>
                            </div>
                            <div class="header-cell">📦 Product</div>
                            <div class="header-cell sortable" @click="sortby('scheduled_date')">
                                <span>📆 Scheduled</span>
                                <span class="sort-icon">{{ getSortIcon('scheduled_date') }}</span>
                            </div>
                            <div class="header-cell sortable" @click="sortby('status')">
                                <span>🏷️ Status</span>
                                <span class="sort-icon">{{ getSortIcon('status') }}</span>
                            </div>
                            <div class="header-cell">⚡ Action</div>
                            <div class="header-cell">🔍 View</div>
                        </div>

                        <div class="table-body">
                            <transition-group name="fade-slide" tag="div">
                                <div v-for="(item, idx) in paginatedItems" :key="item.id" class="table-row" :style="{ animationDelay: `${idx * 50}ms` }">
                                    <div class="row-cell">
                                        <div class="date-wrapper">
                                            <div class="date-day">{{ formatDateDay(item.created_on) }}</div>
                                            <div class="date-month">{{ formatDateMonth(item.created_on) }}</div>
                                            <div class="date-time">{{ formatTime(item.created_on) }}</div>
                                        </div>
                                    </div>
                                    <div class="row-cell">
                                        <span class="invoice-badge">
                                            <span class="invoice-hash">#</span>{{ item.invoice_id }}
                                        </span>
                                    </div>
                                    <div class="row-cell product-cell">
                                        <div class="product-info-expanded">
                                            <div class="product-image-section" v-if="getProductImage(item)">
                                                <div class="product-image-container" @click="openImageModal(getProductImage(item))">
                                                    <img :src="getProductImage(item)" :alt="item.product_name" class="product-thumbnail" @error="handleImageError">
                                                    <div class="image-overlay">
                                                        <span>🔍</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="product-image-section" v-else>
                                                <div class="product-image-placeholder">
                                                    <span>📦</span>
                                                </div>
                                            </div>
                                            <div class="product-details-expanded">
                                                <div class="product-sku">
                                                    <span class="sku-label">SKU:</span>
                                                    <span class="sku-value">{{ item.product_sku }}</span>
                                                </div>
                                                <div class="product-name-expanded" v-if="item.product_name">
                                                    {{ item.product_name }}
                                                </div>
                                                <div class="product-url-section" v-if="item.image_url">
                                                    <a href="#" class="image-url-link" @click.prevent="openImageModal(getProductImage(item))">
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                                            <path d="M18 13V19C18 19.5304 17.7893 20.0391 17.4142 20.4142C17.0391 20.7893 16.5304 21 16 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V8C3 7.46957 3.21071 6.96086 3.58579 6.58579C3.96086 6.21071 4.46957 6 5 6H11M15 3H21M21 3V9M21 3L10 14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                                                        </svg>
                                                        <span>View Product Image</span>
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="row-cell">
                                        <div v-if="item.scheduled_date" class="scheduled-wrapper">
                                            <div class="scheduled-date">{{ formatFullDate(item.scheduled_date) }}</div>
                                            <div class="scheduled-badge" v-if="isUrgent(item.scheduled_date)">⚠️ Urgent</div>
                                        </div>
                                        <span v-else class="na-badge">Not scheduled</span>
                                    </div>
                                    <div class="row-cell">
                                        <div :class="['status-indicator', `status-${item.status}`]">
                                            <span class="status-dot"></span>
                                            <span class="status-text">{{ getStatusText(item.status) }}</span>
                                        </div>
                                    </div>
                                    <div class="row-cell">
                                        <div class="action-badge">{{ getActionText(item.action) }}</div>
                                    </div>
                                    <div class="row-cell">
                                        <button class="view-button" @click="view_ticket(item)">
                                            <span>Details</span>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" stroke-width="2"/>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </transition-group>
                        </div>
                    </div>

                    <div v-else class="empty-state">
                        <div class="empty-animation">
                            <div class="empty-icon">📭</div>
                            <div class="empty-ring"></div>
                        </div>
                        <h3>No Tickets Found</h3>
                        <p>Try adjusting your filters or create a new service ticket</p>
                        <button class="create-ticket-btn" @click="createNewTicket">+ Create New Ticket</button>
                    </div>
                </b-overlay>
            </div>

            <!-- Enhanced Pagination -->
            <div class="pagination-section" v-if="filteredItems.length > 0">
                <div class="pagination-info">
                    Showing <strong>{{ paginationStart }}-{{ paginationEnd }}</strong> of <strong>{{ filteredItems.length }}</strong> tickets
                </div>
                <div class="pagination-controls">
                    <button class="page-btn" :disabled="currentPage === 1" @click="currentPage--">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2"/>
                        </svg>
                    </button>
                    <div class="page-numbers">
                        <button 
                            v-for="page in displayedPages" 
                            :key="page"
                            :class="['page-number', { active: currentPage === page }]"
                            @click="currentPage = page"
                            :disabled="page === '...'"
                        >
                            {{ page }}
                        </button>
                    </div>
                    <button class="page-btn" :disabled="currentPage === totalPages" @click="currentPage++">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2"/>
                        </svg>
                    </button>
                </div>
            </div>
        </div>

        <!-- Enhanced Modal -->
        <b-modal id="modal-cs-ticket" scrollable title="Ticket Details" size="lg" hide-footer centered body-class="p-0">
            <CustomerServiceTicket :viewdata="item_data" />
        </b-modal>

        <!-- Image Preview Modal -->
        <b-modal id="image-preview-modal" title="Product Image" size="lg" hide-footer centered>
            <div class="image-preview-container">
                <img :src="selectedImageUrl" alt="Product Image" class="preview-image" @error="handlePreviewImageError">
                <div class="image-url-display">
                    <strong>Image URL:</strong>
                    <div class="url-wrapper">
                        <a :href="selectedImageUrl" target="_blank" class="image-link">{{ selectedImageUrl }}</a>
                        <button class="copy-url-btn" @click="copyImageUrl" title="Copy URL">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z" fill="currentColor"/>
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
    import moment from 'moment'
    import global_vars from '@/utilities/global_vars'
    import mappings from '@/utilities/mappings'
    import Requestor from '@/utilities/Requestor'
    import CustomerServiceTicket from "@/components/CustomerService/CustomerServiceTicket"

    // AnimatedNumber Component
    const AnimatedNumber = {
        props: ['value', 'duration'],
        data() {
            return {
                displayValue: 0
            }
        },
        watch: {
            value: {
                immediate: true,
                handler(newVal) {
                    this.animateTo(newVal);
                }
            }
        },
        methods: {
            animateTo(newVal) {
                const start = this.displayValue;
                const end = newVal;
                const duration = this.duration || 500;
                const startTime = performance.now();
                
                const update = (currentTime) => {
                    const elapsed = currentTime - startTime;
                    const progress = Math.min(elapsed / duration, 1);
                    const easeOutCubic = 1 - Math.pow(1 - progress, 3);
                    this.displayValue = Math.floor(start + (end - start) * easeOutCubic);
                    
                    if (progress < 1) {
                        requestAnimationFrame(update);
                    }
                };
                
                requestAnimationFrame(update);
            }
        },
        template: '<span>{{ displayValue.toLocaleString() }}</span>'
    }

    export default {
        name: "ListCustomerServiceTickets",
        components: { 
            CustomerServiceTicket,
            AnimatedNumber 
        },
        data: function(){
            return {
                items: [],
                items_being_loaded: false,
                searchQuery: '',
                sortKey: 'created_on',
                sortAsc: false,
                currentPage: 1,
                itemsPerPage: 10,
                showFilters: true,
                selectedImageUrl: '',
                list_options: [
                    {value: "all", text: "ALL", icon: "📊"},
                    {value: "new", text: "NEW", icon: "✨"},
                    {value: "open", text: "OPEN", icon: "🟡"},
                    {value: "assigned", text: "ASSIGNED", icon: "👤"},
                    {value: "in_progress", text: "IN PROGRESS", icon: "⚙️"},
                    {value: "resolved", text: "RESOLVED", icon: "✅"},
                    {value: "exchange", text: "EXCHANGE", icon: "🔄"},
                    {value: "pending", text: "PENDING", icon: "⏳"},
                    {value: "closed", text: "CLOSED", icon: "🔒"}
                ],
                list_option_type: "all",
                item_data: {},
                action_types: [
                    {value: "exchange", text: "EXCHANGE"},
                    {value: "repair_at_ch", text: "ON-SITE REPAIR"},
                    {value: "repair_at_wh", text: "WAREHOUSE REPAIR"},
                    {value: "order_parts_with_mfg", text: "ORDER PARTS"},
                    {value: "not_covered", text: "NOT COVERED"},
                    {value: "covered_under_extended_warranty", text: "WARRANTY"},
                ]
            }
        },
        computed: {
            filteredItems() {
                let filtered = [...this.items];
                
                if (this.searchQuery) {
                    const query = this.searchQuery.toLowerCase();
                    filtered = filtered.filter(item => 
                        item.invoice_id.toString().includes(query) ||
                        item.product_sku.toLowerCase().includes(query) ||
                        (item.product_name && item.product_name.toLowerCase().includes(query))
                    );
                }
                
                filtered.sort((a, b) => {
                    let aVal = a[this.sortKey];
                    let bVal = b[this.sortKey];
                    
                    if (this.sortKey === 'created_on' || this.sortKey === 'scheduled_date') {
                        aVal = new Date(aVal || 0);
                        bVal = new Date(bVal || 0);
                    }
                    
                    if (aVal < bVal) return this.sortAsc ? -1 : 1;
                    if (aVal > bVal) return this.sortAsc ? 1 : -1;
                    return 0;
                });
                
                return filtered;
            },
            paginatedItems() {
                const start = (this.currentPage - 1) * this.itemsPerPage;
                const end = start + this.itemsPerPage;
                return this.filteredItems.slice(start, end);
            },
            totalPages() {
                return Math.ceil(this.filteredItems.length / this.itemsPerPage);
            },
            paginationStart() {
                return (this.currentPage - 1) * this.itemsPerPage + 1;
            },
            paginationEnd() {
                return Math.min(this.currentPage * this.itemsPerPage, this.filteredItems.length);
            },
            displayedPages() {
                const delta = 2;
                const range = [];
                const rangeWithDots = [];
                let l;

                for (let i = 1; i <= this.totalPages; i++) {
                    if (i === 1 || i === this.totalPages || (i >= this.currentPage - delta && i <= this.currentPage + delta)) {
                        range.push(i);
                    }
                }

                range.forEach((i) => {
                    if (l) {
                        if (i - l === 2) {
                            rangeWithDots.push(l + 1);
                        } else if (i - l !== 1) {
                            rangeWithDots.push('...');
                        }
                    }
                    rangeWithDots.push(i);
                    l = i;
                });

                return rangeWithDots;
            },
            ticketStats() {
                const total = this.items.length;
                const open = this.items.filter(i => ['new', 'open', 'assigned', 'in_progress'].includes(i.status)).length;
                const resolved = this.items.filter(i => i.status === 'resolved').length;
                const pending = this.items.filter(i => i.status === 'pending').length;
                
                // Calculate trends (mock data - replace with actual trend calculation)
                const previousTotal = total * 0.9; // Mock previous total
                const totalTrend = previousTotal ? Math.round(((total - previousTotal) / previousTotal) * 100) : 0;
                
                return [
                    { label: 'Total Tickets', value: total, emoji: '📊', color: 'total', trend: totalTrend },
                    { label: 'Open Tickets', value: open, emoji: '🟡', color: 'open', trend: -5 },
                    { label: 'Resolved', value: resolved, emoji: '✅', color: 'resolved', trend: 8 },
                    { label: 'Pending', value: pending, emoji: '⏳', color: 'pending', trend: 0 }
                ];
            }
        },
        watch: {
            searchQuery() {
                this.currentPage = 1;
            },
            filteredItems() {
                this.currentPage = 1;
            }
        },
        methods: {
            getProductImage(item) {
                if (!item || !item.image_url) {
                    return null;
                }

                let imageUrl = item.image_url.trim();

                if (imageUrl === '' || imageUrl === 'null' || imageUrl === 'undefined') {
                    return null;
                }

                // Use proxy for fmapi.myfurnituremecca.com images
                if (imageUrl.includes('fmapi.myfurnituremecca.com')) {
                    return `/api/image/image-proxy/?url=${encodeURIComponent(imageUrl)}`;
                }

                return imageUrl;
            },
            formatDateDay(date) {
                if (!date) return '—';
                return moment(date).format('DD');
            },
            formatDateMonth(date) {
                if (!date) return '—';
                return moment(date).format('MMM');
            },
            formatTime(date) {
                if (!date) return '';
                return moment(date).format('h:mm A');
            },
            formatFullDate(date) {
                if (!date) return '—';
                return moment(date).format('MMM DD, YYYY');
            },
            getStatusText(status) {
                const option = this.list_options.find(opt => opt.value === status);
                return option ? option.text : status;
            },
            getStatusIcon(status) {
                const option = this.list_options.find(opt => opt.value === status);
                return option ? option.icon : '📋';
            },
            getStatusCount(status) {
                if (status === 'all') return this.items.length;
                return this.items.filter(i => i.status === status).length;
            },
            getActionText(action) {
                const option = this.action_types.find(opt => opt.value === action);
                return option ? option.text : action;
            },
            getSortIcon(key) {
                if (this.sortKey !== key) return '↕️';
                return this.sortAsc ? '↑' : '↓';
            },
            sortby(key) {
                if (this.sortKey === key) {
                    this.sortAsc = !this.sortAsc;
                } else {
                    this.sortKey = key;
                    this.sortAsc = true;
                }
            },
            isUrgent(date) {
                const scheduledDate = moment(date);
                const today = moment();
                const daysDiff = scheduledDate.diff(today, 'days');
                return daysDiff <= 2 && daysDiff >= 0;
            },
            view_ticket(item) {
                this.item_data = { id: item.id };
                this.$bvModal.show("modal-cs-ticket");
            },
            createNewTicket() {
                this.$bvToast.toast("Create new ticket functionality coming soon!", {
                    title: "Info", 
                    variant: "info"
                });
            },
            openImageModal(imageUrl) {
                this.selectedImageUrl = imageUrl;
                this.$bvModal.show("image-preview-modal");
            },
            handleImageError(e) {
                e.target.style.display = 'none';
                const container = e.target.parentElement;
                if (container) {
                    const placeholder = document.createElement('div');
                    placeholder.className = 'image-error-placeholder';
                    placeholder.innerHTML = '🖼️<br>Failed to load';
                    container.appendChild(placeholder);
                }
            },
            handlePreviewImageError(e) {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML += '<div class="image-error-message">Failed to load image. Please check the URL.</div>';
            },
            copyImageUrl() {
                navigator.clipboard.writeText(this.selectedImageUrl).then(() => {
                    this.$bvToast.toast("Image URL copied to clipboard!", {
                        title: "Success",
                        variant: "success",
                        autoHideDelay: 2000
                    });
                }).catch(() => {
                    this.$bvToast.toast("Failed to copy URL", {
                        title: "Error",
                        variant: "warning"
                    });
                });
            },
            fetch_items() {
                let that = this;
                let path = "/api/customer_service/list_tickets/"
                let data = { type: this.list_option_type }

                let s = function(response) {
                    if (response.data.result) {
                        that.items = response.data.result;
                    } else {
                        that.$bvToast.toast(response.data.error, {title: "Error", variant: "warning"});
                    }
                    that.items_being_loaded = false;
                }
                let f = function(error) {
                    that.$bvToast.toast("Error loading tickets.", {title: "Error", variant: "warning"});
                    that.items_being_loaded = false;
                }

                this.items = [];
                this.items_being_loaded = true;
                Requestor.getSyncP(path, data, this, s, f);
            }
        },
        created() {
            this.fetch_items();
        }
    }
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

.inventory-tickets {
    max-width: 1600px;
    margin: 0 auto;
    padding: 32px;
    background: linear-gradient(135deg, #f5f7fa 0%, #f8fafc 100%);
    min-height: 100vh;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* Stats Panel - Modern Cards */
.stats-panel {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;
    margin-bottom: 32px;
}

.stat-card {
    background: white;
    border-radius: 28px;
    padding: 24px;
    display: flex;
    align-items: center;
    gap: 20px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
}

.stat-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #667eea, #764ba2);
    transform: translateY(-100%);
    transition: transform 0.3s ease;
}

.stat-card:hover {
    transform: translateY(-6px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
}

.stat-card:hover::before {
    transform: translateY(0);
}

.stat-card.total::before { background: linear-gradient(90deg, #667eea, #764ba2); }
.stat-card.open::before { background: linear-gradient(90deg, #f59e0b, #ef4444); }
.stat-card.resolved::before { background: linear-gradient(90deg, #10b981, #34d399); }
.stat-card.pending::before { background: linear-gradient(90deg, #8b5cf6, #a78bfa); }

.stat-icon-wrapper {
    width: 64px;
    height: 64px;
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(102, 126, 234, 0.1);
}

.stat-icon {
    font-size: 32px;
}

.stat-content {
    flex: 1;
}

.stat-number {
    font-size: 36px;
    font-weight: 800;
    color: #0f172a;
    line-height: 1.2;
    letter-spacing: -0.02em;
}

.stat-label {
    font-size: 14px;
    font-weight: 600;
    color: #64748b;
    margin-top: 4px;
    letter-spacing: 0.3px;
}

.stat-trend {
    margin-top: 6px;
    font-size: 12px;
    font-weight: 600;
}

.trend-up { color: #10b981; }
.trend-down { color: #ef4444; }

/* Main Card */
.tickets-card {
    background: white;
    border-radius: 32px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(226, 232, 240, 0.6);
}

/* Card Header */
.card-header {
    padding: 32px;
    border-bottom: 1px solid #f1f5f9;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 20px;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 24px;
}

.title-wrapper {
    display: flex;
    align-items: center;
    gap: 12px;
}

.title-icon {
    font-size: 32px;
}

.card-title {
    font-size: 28px;
    font-weight: 800;
    background: linear-gradient(135deg, #667eea, #764ba2);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin: 0;
    letter-spacing: -0.5px;
}

.ticket-count-pill {
    background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
    padding: 8px 16px;
    border-radius: 40px;
    display: flex;
    align-items: baseline;
    gap: 6px;
}

.count-number {
    font-size: 20px;
    font-weight: 800;
    color: #0f172a;
}

.count-label {
    font-size: 13px;
    font-weight: 600;
    color: #475569;
}

/* Search */
.header-right {
    display: flex;
    gap: 12px;
    align-items: center;
}

.search-wrapper {
    position: relative;
}

.search-icon {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    transition: color 0.2s;
}

.search-input {
    padding: 12px 40px 12px 44px;
    border: 2px solid #e2e8f0;
    border-radius: 48px;
    font-size: 14px;
    width: 280px;
    outline: none;
    transition: all 0.2s;
    background: white;
    font-weight: 500;
    font-family: 'Inter', sans-serif;
}

.search-input:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
}

.search-clear {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    padding: 4px;
    border-radius: 50%;
    transition: all 0.2s;
}

.search-clear:hover {
    color: #475569;
    background: #f1f5f9;
}

.filter-toggle {
    width: 44px;
    height: 44px;
    border-radius: 48px;
    background: #f8fafc;
    border: 2px solid #e2e8f0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
}

.filter-toggle:hover {
    background: #f1f5f9;
    border-color: #667eea;
}

/* Filter Section */
.filter-section {
    padding: 0 32px;
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.3s ease-out;
}

.filter-section.expanded {
    max-height: 200px;
    padding: 20px 32px;
}

.filter-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
}

.filter-chip {
    padding: 8px 20px;
    background: #f8fafc;
    border: 2px solid #e2e8f0;
    border-radius: 48px;
    font-size: 13px;
    font-weight: 600;
    color: #475569;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 8px;
    letter-spacing: 0.3px;
}

.filter-chip:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
    transform: translateY(-2px);
}

.filter-chip.active {
    background: linear-gradient(135deg, #667eea, #764ba2);
    border-color: transparent;
    color: white;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.chip-icon {
    font-size: 14px;
}

.chip-count {
    background: rgba(0, 0, 0, 0.1);
    padding: 2px 8px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
}

.filter-chip.active .chip-count {
    background: rgba(255, 255, 255, 0.2);
}

/* Table */
.table-container {
    overflow-x: auto;
}

.tickets-table {
    min-width: 1300px;
}

    .table-header {
        display: grid;
        grid-template-columns: 140px 120px 1.2fr 140px 140px 160px 100px;
        background: #3b82f6;
        color: white !important;
        padding: 16px 24px;
        border-bottom: 2px solid #eef2ff;
        font-size: 13px;
        font-weight: 700;
        color: #4b5563;
        text-transform: uppercase;
        letter-spacing: 0.5px;
           outline: 2px solid #3b82f6;       
    outline-offset: 2px;   
    }

.header-cell {
    cursor: pointer;
    user-select: none;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: color 0.2s;
}

.header-cell.sortable:hover {
    color: #667eea;
}

.sort-icon {
    font-size: 12px;
    opacity: 0.6;
}

.table-body {
    background: white;
}

.table-row {
    display: grid;
    grid-template-columns: 140px 120px 1.2fr 140px 140px 160px 100px;
    padding: 20px 24px;
    border-bottom: 1px solid #f1f5f9;
    align-items: center;
    transition: all 0.2s;
    animation: fadeSlideIn 0.4s ease-out forwards;
    opacity: 0;
    transform: translateX(-10px);
}

@keyframes fadeSlideIn {
    to {
        opacity: 1;
        transform: translateX(0);
    }
}

.table-row:hover {
    background: linear-gradient(90deg, #fefce8, #ffffff);
    transform: scale(1.01);
}

/* Expanded Product Info */
.product-cell {
    padding: 12px 0;
}

.product-info-expanded {
    display: flex;
    gap: 16px;
    align-items: flex-start;
}

.product-image-section {
    flex-shrink: 0;
}

.product-image-container {
    position: relative;
    width: 110px;
    height: 70px;
    border-radius: 12px;
    overflow: hidden;
    cursor: pointer;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    transition: all 0.2s;
}

.product-thumbnail {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.2s;
}

.product-image-container:hover .product-thumbnail {
    transform: scale(1.1);
}

.image-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s;
    color: white;
    font-size: 24px;
}

.product-image-container:hover .image-overlay {
    opacity: 1;
}

.product-image-placeholder {
    width: 110px;
    height: 70px;
    border-radius: 12px;
    background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 32px;
    color: #94a3b8;
    border: 1px solid #e2e8f0;
}

.image-error-placeholder {
    width: 70px;
    height: 70px;
    border-radius: 12px;
    background: #fef2f2;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    font-size: 20px;
    color: #ef4444;
    text-align: center;
    line-height: 1.2;
    font-size: 12px;
    padding: 4px;
}

.product-details-expanded {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.product-sku {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.sku-label {
    font-size: 11px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.sku-value {
    font-family: 'SF Mono', monospace;
    font-size: 13px;
    font-weight: 700;
    color: #667eea;
    background: #eef2ff;
    padding: 4px 10px;
    border-radius: 8px;
    display: inline-block;
}

.product-name-expanded {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
    line-height: 1.4;
    word-break: break-word;
}

.product-url-section {
    margin-top: 4px;
}

.image-url-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #3b82f6;
    text-decoration: none;
    background: #eff6ff;
    padding: 5px 12px;
    border-radius: 8px;
    transition: all 0.2s;
    width: fit-content;
    font-weight: 500;
}

.image-url-link:hover {
    background: #dbeafe;
    color: #2563eb;
    transform: translateY(-1px);
}

/* Date Wrapper */
.date-wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.date-day {
    font-size: 20px;
    font-weight: 800;
    color: #0f172a;
    line-height: 1;
}

.date-month {
    font-size: 11px;
    font-weight: 700;
    color: #667eea;
    text-transform: uppercase;
    margin: 4px 0 2px;
}

.date-time {
    font-size: 11px;
    font-weight: 500;
    color: #94a3b8;
}

/* Invoice Badge */
.invoice-badge {
    font-family: 'SF Mono', monospace;
    background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
    padding: 6px 12px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
    display: inline-block;
}

.invoice-hash {
    color: #94a3b8;
    font-weight: 600;
}

/* Scheduled Wrapper */
.scheduled-wrapper {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.scheduled-date {
    font-size: 13px;
    font-weight: 600;
    color: #334155;
}

.scheduled-badge {
    font-size: 10px;
    font-weight: 700;
    color: #dc2626;
    background: #fee2e2;
    padding: 2px 8px;
    border-radius: 20px;
    width: fit-content;
}

.na-badge {
    color: #94a3b8;
    font-size: 12px;
    font-weight: 500;
    font-style: italic;
}

/* Status Indicator */
.status-indicator {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 14px;
    border-radius: 32px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.3px;
}

.status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: currentColor;
}

.status-new { background: #eff6ff; color: #1e40af; }
.status-open { background: #fffbeb; color: #b45309; }
.status-assigned { background: #e0f2fe; color: #0369a1; }
.status-in_progress { background: #fef3c7; color: #b45309; }
.status-resolved { background: #d1fae5; color: #065f46; }
.status-exchange { background: #ffedd5; color: #9a3412; }
.status-pending { background: #fef9c3; color: #854d0e; }
.status-closed { background: #f1f5f9; color: #334155; }

/* Action Badge */
.action-badge {
    font-size: 11px;
    font-weight: 700;
    background: #f8fafc;
    padding: 6px 12px;
    border-radius: 20px;
    display: inline-block;
    white-space: nowrap;
    color: #475569;
    border: 1px solid #e2e8f0;
}

/* View Button */
.view-button {
    padding: 8px 16px;
    background: linear-gradient(135deg, #667eea, #764ba2);
    border: none;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 700;
    color: white;
    cursor: pointer;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.view-button:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

/* Image Preview Modal */
.image-preview-container {
    text-align: center;
    padding: 20px;
}

.preview-image {
    max-width: 100%;
    max-height: 70vh;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    margin-bottom: 20px;
}

.image-url-display {
    margin-top: 20px;
    padding: 16px;
    background: #f8fafc;
    border-radius: 12px;
    text-align: left;
}

.image-url-display strong {
    display: block;
    margin-bottom: 8px;
    color: #0f172a;
    font-size: 13px;
    font-weight: 700;
}

.url-wrapper {
    display: flex;
    align-items: center;
    gap: 12px;
    background: white;
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
}

.image-link {
    color: #3b82f6;
    text-decoration: none;
    font-size: 12px;
    font-family: monospace;
    word-break: break-all;
    flex: 1;
}

.image-link:hover {
    text-decoration: underline;
}

.copy-url-btn {
    padding: 6px;
    background: #f1f5f9;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    flex-shrink: 0;
}

.copy-url-btn:hover {
    background: #e2e8f0;
    transform: scale(1.05);
}

.image-error-message {
    color: #ef4444;
    font-size: 14px;
    padding: 20px;
    text-align: center;
}

/* Empty State */
.empty-state {
    text-align: center;
    padding: 80px 20px;
}

.empty-animation {
    position: relative;
    display: inline-block;
    margin-bottom: 24px;
}

.empty-icon {
    font-size: 80px;
    animation: float 3s ease-in-out infinite;
}

.empty-ring {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100px;
    height: 100px;
    border: 2px solid rgba(102, 126, 234, 0.2);
    border-radius: 50%;
    animation: pulse 2s ease-in-out infinite;
}

@keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
}

@keyframes pulse {
    0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.5; }
    50% { transform: translate(-50%, -50%) scale(1.2); opacity: 0; }
}

.empty-state h3 {
    font-size: 24px;
    font-weight: 800;
    color: #1e293b;
    margin: 0 0 12px;
}

.empty-state p {
    font-size: 16px;
    color: #64748b;
    margin: 0 0 24px;
}

.create-ticket-btn {
    padding: 12px 28px;
    background: linear-gradient(135deg, #667eea, #764ba2);
    border: none;
    border-radius: 48px;
    font-size: 14px;
    font-weight: 700;
    color: white;
    cursor: pointer;
    transition: all 0.2s;
}

.create-ticket-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
}

/* Pagination */
.pagination-section {
    padding: 24px 32px;
    border-top: 1px solid #f1f5f9;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
}

.pagination-info {
    font-size: 13px;
    color: #64748b;
    font-weight: 500;
}

.pagination-info strong {
    color: #0f172a;
    font-weight: 700;
}

.pagination-controls {
    display: flex;
    gap: 12px;
    align-items: center;
}

.page-btn {
    width: 36px;
    height: 36px;
    border-radius: 12px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
    background: #667eea;
    border-color: #667eea;
    color: white;
    transform: translateY(-1px);
}

.page-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.page-numbers {
    display: flex;
    gap: 6px;
}

.page-number {
    min-width: 36px;
    height: 36px;
    border-radius: 12px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    color: #475569;
    transition: all 0.2s;
}

.page-number:hover:not(.active):not(:disabled) {
    background: #f1f5f9;
    border-color: #cbd5e1;
}

.page-number.active {
    background: linear-gradient(135deg, #667eea, #764ba2);
    border-color: transparent;
    color: white;
}

.page-number:disabled {
    cursor: default;
    background: transparent;
    border-color: transparent;
}

/* Transitions */
.fade-slide-enter-active,
.fade-slide-leave-active {
    transition: all 0.3s ease;
}

.fade-slide-enter,
.fade-slide-leave-to {
    opacity: 0;
    transform: translateY(20px);
}

/* Responsive */
@media (max-width: 1200px) {
    .inventory-tickets {
        padding: 20px;
    }
    
    .stats-panel {
        grid-template-columns: repeat(2, 1fr);
        gap: 16px;
    }
    
    .table-header, .table-row {
        grid-template-columns: 120px 100px 1.2fr 120px 120px 140px 90px;
    }
}

@media (max-width: 768px) {
    .inventory-tickets {
        padding: 16px;
    }
    
    .stats-panel {
        grid-template-columns: 1fr;
        gap: 12px;
    }
    
    .card-header {
        flex-direction: column;
        align-items: stretch;
        padding: 20px;
    }
    
    .header-left {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
    }
    
    .title-wrapper {
        justify-content: space-between;
    }
    
    .ticket-count-pill {
        width: fit-content;
    }
    
    .search-input {
        width: 100%;
    }
    
    .filter-section.expanded {
        max-height: 300px;
        padding: 16px 20px;
    }
    
    .filter-tabs {
        flex-wrap: nowrap;
        overflow-x: auto;
        padding-bottom: 8px;
    }
    
    .filter-chip {
        white-space: nowrap;
    }
    
    .card-title {
        font-size: 24px;
    }
    
    .stat-number {
        font-size: 28px;
    }
    
    .pagination-section {
        flex-direction: column;
        align-items: stretch;
        padding: 20px;
    }
    
    .pagination-controls {
        justify-content: center;
    }
    
    .page-numbers {
        flex-wrap: wrap;
        justify-content: center;
    }
    
    .product-info-expanded {
        flex-direction: column;
    }
    
    .product-image-container,
    .product-image-placeholder {
        width: 60px;
        height: 60px;
    }
}
</style>


// WEBPACK FOOTER //
// src/components/CustomerService/ListCustomerServiceTickets.vue